import {Range} from 'notebook/tables/excess/FunctionConstants';
import {NumericalError} from 'notebook/tables/excess/Util';
import {TypedValue} from 'notebook/tables/TableConstants';
import {coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';

import createLookupLists from './createLookupLists';

/**
 * Searches the provided search range to find the value matching the provided search key. This will first
 * attempt to match the TypedValues based off of the ID that they map to, and if that fails, it will try to
 * match it based off of their name
 * @param searchKey The TypedValue target we want to find in the given search columns
 * @param searchRange The 2D array of EvaluablePrimitives/TypedValues in the given search columns & rows
 * @param resultRange The 2D array of EvaluablePrimitives/TypedValues in the given result columns & rows. A null value
 * for resultRange is used to decide if we will use VLOOKUP or HLOOKUP (based on the shape of searchRange) behavior
 * for the LOOKUP function. Null should be used sparingly in other contexts as it causes confusing product behavior.
 * @returns The result will return the matching value if found and undefined if not. The search/resulList contain
 * the arrays of EvaluablePrimitives/TypedValues values in the given search and result ranges
 */
export function lookupTypedValue(
  searchKey: TypedValue,
  searchRange: Range<TypedValue>,
  resultRange: Range<TypedValue>,
  functionName: string
): {result: TypedValue | undefined; searchList: Array<TypedValue>; resultList: Array<TypedValue>} {
  const searchKeyPrimitive = coerceToPrimitive(searchKey, {shouldUseIds: true});
  const {searchList, resultList} = createLookupLists<TypedValue>(searchRange, resultRange);
  // First, try searching for the searchKeyPrimitive by matching the TypedValues in the range based off of
  // their ID
  const rawSearchList = searchList.map((typedValue: TypedValue) =>
    coerceToPrimitive(typedValue, {shouldUseIds: true})
  );
  let index = rawSearchList.indexOf(searchKeyPrimitive);
  if (index < 0) {
    // If no matching value is found by ID, then search by name.
    const searchKeyName = coerceToPrimitive(searchKey, {shouldUseIds: false});
    const rawSearchListWithName = searchList.map((typedValue: TypedValue) => coerceToPrimitive(typedValue));
    index = rawSearchListWithName.indexOf(searchKeyName);
  }
  if (index >= resultList.length) {
    // dimension mismatch, Excel extends the range; but we fail like Sheets
    throw new NumericalError(`${functionName} evaluates to an out of range value ${index}.`);
  }
  return {result: resultList[index], searchList, resultList};
}
